import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  Grid,
  Chip,
  useTheme,
  keyframes,
  styled,
  Paper,
  Rating,
  Avatar,
  CircularProgress
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  PlayArrowRounded,
  TrendingUpRounded,
  CheckCircleRounded,
  StarRounded,
  SecurityRounded,
  FlashOnRounded,
  EnergySavingsLeaf,
  FormatQuote
} from '@mui/icons-material';

// Keyframe animations
const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 8px 32px rgba(0, 188, 212, 0.3); }
  50% { box-shadow: 0 12px 48px rgba(0, 188, 212, 0.6); }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
`;

// Styled Components
const CTAContainer = styled(Box)(({ theme }) => ({
  background: `
    radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}08 30%, transparent 50%),
    radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}12 0%, ${theme.palette.secondary.main}06 30%, transparent 50%),
    linear-gradient(135deg, ${theme.palette.background.paper}90 0%, ${theme.palette.background.paper}75 40%, ${theme.palette.primary.main}10 75%, ${theme.palette.primary.main}08 100%)
  `,
  borderRadius: '32px',
  padding: '60px 40px',
  border: `2px solid ${theme.palette.primary.main}30`,
  backdropFilter: 'blur(20px)',
  boxShadow: `0 20px 60px ${theme.palette.primary.main}20`,
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
    borderRadius: '32px 32px 0 0',
  }
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  padding: '18px 36px',
  fontSize: '1.2rem',
  fontWeight: 700,
  borderRadius: '16px',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  textTransform: 'none',
  animation: `${glow} 3s ease-in-out infinite`,

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
    transition: 'left 0.6s',
  },

  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
    transform: 'translateY(-3px) scale(1.02)',
    boxShadow: `0 15px 50px ${theme.palette.primary.main}60`,
    animation: 'none',

    '&::before': {
      left: '100%',
    }
  },

  '&:active': {
    transform: 'translateY(-1px) scale(0.98)',
  }
}));

const StatsCard = styled(Card)(({ theme }) => ({
  padding: '20px',
  borderRadius: '16px',
  background: `${theme.palette.background.paper}90`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${theme.palette.primary.main}20`,
  textAlign: 'center',
  transition: 'all 0.3s ease',

  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: `0 12px 32px ${theme.palette.primary.main}25`,
    border: `1px solid ${theme.palette.primary.main}40`,
  }
}));

const UrgencyBadge = styled(Chip)(({ theme }) => ({
  background: `${theme.palette.primary.main}15`,
  border: `1px solid ${theme.palette.primary.main}30`,
  color: theme.palette.primary.main,
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: theme.palette.primary.main,
  }
}));

interface Testimonial {
  feedback_id: string;
  name: string;
  company: string | null;
  rating: number;
  feedback_text: string;
  created_at: string;
}

const ConversionCTA: React.FC = () => {
  const theme = useTheme();
  const [timeLeft, setTimeLeft] = useState(3600); // 1 hour in seconds
  const [isHovered, setIsHovered] = useState(false);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);

  const stats = [
    {
      number: "Beta",
      label: "Testphase läuft",
      icon: <CheckCircleRounded />,
      color: "success"
    },
    {
      number: "100%",
      label: "Kostenlos testen",
      icon: <StarRounded />,
      color: "primary"
    },
    {
      number: "1-2 Minuten",
      label: "Geschätzte Analysezeit",
      icon: <FlashOnRounded />,
      color: "info"
    },
    {
      number: "Bis 40%",
      label: "Mögliche CO₂-Reduktion*",
      icon: <EnergySavingsLeaf />,
      color: "success"
    }
  ];

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 3600);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch('/api/feedback/published?limit=6');
        if (response.ok) {
          const data = await response.json();
          setTestimonials(data);
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoadingTestimonials(false);
      }
    };

    fetchTestimonials();
  }, []);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{
      pt: 10,
      pb: 3,  // 24px spacing before footer
      position: 'relative'
    }}>
      {/* Background Effects */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 30% 70%, ${theme.palette.primary.main}06 0%, transparent 50%)`,
          animation: `${float} 8s ease-in-out infinite`,
          zIndex: 0,
        }}
      />

      <Container maxWidth={false} sx={{ position: 'relative', zIndex: 2, px: { xs: 2, sm: 4, md: 6 }, maxWidth: '1400px', mx: 'auto' }}>
        {/* Social Proof Stats */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Box
                sx={{
                  animation: `${slideInUp} 1s ease-out ${index * 0.1}s both`,
                }}
              >
                <StatsCard>
                  <Box
                    sx={{
                      color: `${stat.color}.main`,
                      mb: 1,
                      animation: `${pulse} 2s infinite`,
                      animationDelay: `${index * 0.5}s`,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: `${stat.color}.main`,
                      mb: 1,
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </StatsCard>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Main CTA */}
        <Box sx={{ animation: `${slideInUp} 1s ease-out 0.5s both` }}>
          <CTAContainer>
            {/* Urgency Badge */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <UrgencyBadge
                icon={<FlashOnRounded />}
                label={`⏰ Limitierte Beta-Phase noch ${formatTime(timeLeft)}`}
                size="medium"
              />
            </Box>

            {/* Main Headline */}
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                fontWeight: 800,
                mb: 3,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Bereit für die Energiewende?
            </Typography>

            {/* Subheadline */}
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                textAlign: 'center',
                mb: 4,
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Testen Sie <strong>JETZT</strong> unsere kostenlose Beta-Analyse und entdecken Sie
              Potentiale von Hybrid-Speichersystemen für Ihr Unternehmen.
            </Typography>

            {/* Benefits List */}
            <Grid container spacing={2} sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
              {[
                '✅ 100% kostenlose Beta-Version',
                '✅ Geschätzte Ergebnisse in 1-2 Minuten',
                '✅ Keine Registrierung erforderlich',
                '✅ DSGVO-konforme Datenverarbeitung',
                '✅ Potentiale für Kosteneinsparung identifizieren',
                '✅ Systematische Analyse-Empfehlungen'
              ].map((benefit, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      animation: `${slideInUp} 1s ease-out ${0.8 + index * 0.1}s both`,
                    }}
                  >
                    {benefit}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* CTA Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                justifyContent: 'center',
                flexWrap: 'wrap',
                mb: 6,
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Button
                component={Link}
                to="/upload"
                variant="contained"
                size="large"
                startIcon={<PlayArrowRounded />}
                sx={{
                  padding: '18px 36px',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  borderRadius: '16px',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
                  textTransform: 'none',
                  animation: isHovered ? 'none' : `${bounce} 2s infinite`,
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                    transform: 'translateY(-3px) scale(1.02)',
                    boxShadow: `0 15px 50px ${theme.palette.primary.main}60`,
                  }
                }}
              >
                🚀 Kostenlose Analyse starten
              </Button>
            </Box>

            {/* Trust Signals */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {[
                  { icon: <SecurityRounded />, label: 'ISO 27001' },
                  { icon: <CheckCircleRounded />, label: 'DSGVO konform' },
                  { icon: <StarRounded />, label: '256-bit SSL' }
                ].map((trust, index) => (
                  <Chip
                    key={index}
                    icon={trust.icon}
                    label={trust.label}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '& .MuiChip-icon': {
                        color: 'primary.main'
                      }
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Ihre Daten sind bei uns sicher • Keine Weitergabe an Dritte • SSL-verschlüsselte Übertragung
              </Typography>
            </Box>

            {/* Money-Back Guarantee */}
            <Box
              sx={{
                textAlign: 'center',
                p: 3,
                borderRadius: 3,
                background: `${theme.palette.success.main}10`,
                border: `2px solid ${theme.palette.success.main}30`,
              }}
            >
              <Typography
                variant="h6"
                color="success.main"
                sx={{ fontWeight: 600, mb: 1 }}
              >
                💰 100% Risiko-frei
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Komplett kostenlose Analyse ohne versteckte Kosten oder Verpflichtungen.
                Testen Sie unser Analyse-Tool völlig unverbindlich!
              </Typography>
            </Box>
          </CTAContainer>
        </Box>

        {/* Customer Testimonials - Hidden until we have real customers */}
        {/* {!loadingTestimonials && testimonials.length > 0 && (
          <Box sx={{ mt: 8, animation: `${slideInUp} 1s ease-out 1s both` }}>
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                fontWeight: 600,
                mb: 4,
                color: 'text.primary',
              }}
            >
              Das sagen unsere Kunden
            </Typography>

            <Grid container spacing={4}>
              {testimonials.map((testimonial, index) => (
                <Grid item xs={12} md={6} lg={4} key={testimonial.feedback_id}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '24px',
                      background: `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.background.paper}85 40%, ${theme.palette.primary.main}08 75%, ${theme.palette.primary.main}05 100%)`,
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${theme.palette.primary.main}20`,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      animation: `${slideInUp} 0.8s ease-out ${index * 0.1}s both`,
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${theme.palette.primary.main}25`,
                      },
                    }}
                  >
                    <FormatQuote
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        fontSize: 60,
                        color: theme.palette.primary.main,
                        opacity: 0.1,
                      }}
                    />

                    <Box sx={{ mb: 2 }}>
                      <Rating
                        value={testimonial.rating}
                        readOnly
                        sx={{
                          '& .MuiRating-iconFilled': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body1"
                      sx={{
                        mb: 3,
                        fontStyle: 'italic',
                        color: 'text.primary',
                        lineHeight: 1.7,
                        minHeight: '100px',
                      }}
                    >
                      "{testimonial.feedback_text}"
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          width: 50,
                          height: 50,
                          fontSize: '1.2rem',
                          fontWeight: 600,
                        }}
                      >
                        {testimonial.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: 'text.primary' }}
                        >
                          {testimonial.name}
                        </Typography>
                        {testimonial.company && (
                          <Typography variant="body2" color="text.secondary">
                            {testimonial.company}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )} */}

        {/* Feedback CTA */}
        <Box sx={{ mt: 8, textAlign: 'center', animation: `${slideInUp} 1s ease-out 1.2s both` }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: 'text.primary',
            }}
          >
            Teilen Sie Ihre Erfahrung
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Helfen Sie uns, Smart Storage Checker zu verbessern. Ihr Feedback ist wertvoll!
          </Typography>
          <Button
            component={Link}
            to="/feedback"
            variant="contained"
            size="large"
            sx={{
              padding: '16px 48px',
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '12px',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
              textTransform: 'none',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 48px ${theme.palette.primary.main}60`,
              }
            }}
          >
            Feedback geben
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ConversionCTA;